/**
 * Canonical in-memory representation for "a week of 15-minute time slots".
 * Mirrors server/services/timeslots.py 1:1 — see that module's docstring for
 * the full shape catalogue. This module is ONLY the conversion boundary; it
 * does not change any DB column, JSONB shape, or API request/response shape.
 * Callers still read/write the exact same wire shapes as today.
 *
 * Built ON TOP OF the (previously unused) domain types in ./types/index.ts —
 * this file does not redefine TimeSlot/DayIndex/AvailabilityMap/ScheduleMap/
 * ScheduleSlot, it imports and operates on them.
 */
import type { TimeSlot, DayIndex, AvailabilityMap, ScheduleMap, ScheduleSlot } from "../types";

export const TIME_SLOT_MINUTES = 15;
export const SLOTS_PER_HOUR = 60 / TIME_SLOT_MINUTES; // 4
export const SLOTS_PER_DAY = 24 * SLOTS_PER_HOUR; // 96
export const DAYS_IN_WEEK = 7;
export const TOTAL_SLOTS = DAYS_IN_WEEK * SLOTS_PER_DAY; // 672

export const DAY_KEYS: DayIndex[] = ["0", "1", "2", "3", "4", "5", "6"];
export const DAY_NAMES_ENGLISH = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;
export const DAY_NAMES_GERMAN_UPPER = ["MONTAG", "DIENSTAG", "MITTWOCH", "DONNERSTAG", "FREITAG", "SAMSTAG", "SONNTAG"] as const;
export const DAY_SHORT_NAMES_DE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;

/** 96 slot strings "0.00".."23.45", the values duplicated 3x today as completeTimesList. */
export const ALL_TIME_SLOTS: TimeSlot[] = (() => {
  const slots: TimeSlot[] = [];
  for (let i = 0; i < SLOTS_PER_DAY; i++) {
    slots.push(formatHHMM(...minutesToHourMinute(i * TIME_SLOT_MINUTES)));
  }
  return slots;
})();

/** German day names in index order, the values duplicated 3x today as completeDaysList. */
export const ALL_DAY_NAMES_DE: string[] = [...DAY_NAMES_GERMAN_UPPER];

export type GlobalSlot = number; // 0..671
export type SlotSet = Set<GlobalSlot>;
/** Occupied slot -> member_id. */
export type SlotAssignment = Map<GlobalSlot, string>;

export class InvalidTimeSlotError extends Error {}

function minutesToHourMinute(totalMinutes: number): [number, number] {
  return [Math.floor(totalMinutes / 60), totalMinutes % 60];
}

/** Parses "HH.MM" or "H.MM" (leading zero on the hour optional) into [hour, minute]. */
export function parseHHMM(time: TimeSlot): [number, number] {
  const parts = typeof time === "string" ? time.split(".") : null;
  if (!parts || parts.length !== 2 || parts[0] === "" || parts[1] === "") {
    throw new InvalidTimeSlotError(`Ungültiges Zeitformat: ${JSON.stringify(time)}`);
  }
  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
    throw new InvalidTimeSlotError(`Ungültiges Zeitformat: ${JSON.stringify(time)}`);
  }
  if (!(hour >= 0 && hour < 24 && minute >= 0 && minute < 60 && minute % TIME_SLOT_MINUTES === 0)) {
    throw new InvalidTimeSlotError(`Zeit außerhalb des 15-Minuten-Rasters: ${JSON.stringify(time)}`);
  }
  return [hour, minute];
}

/** Canonical output: never pads the hour (e.g. "9.00", not "09.00"). */
export function formatHHMM(hour: number, minute: number): TimeSlot {
  return `${hour}.${String(minute).padStart(2, "0")}`;
}

/** Zeitfenster ab `start` über `slotCount` Viertelstunden, z. B. „9.15 – 10.00“.
 *  Das Ende ist exklusiv und darf 24.00 sein. */
export function formatSlotSpan(start: TimeSlot, slotCount: number): string {
  const [hour, minute] = parseHHMM(start);
  const [endHour, endMinute] = minutesToHourMinute(hour * 60 + minute + slotCount * TIME_SLOT_MINUTES);
  return `${formatHHMM(hour, minute)} – ${formatHHMM(endHour, endMinute)}`;
}

/** Converts day index (number or "0"-"6") + "HH.MM" into a global slot index (0..671). */
export function dayTimeToSlot(day: DayIndex | number | string, time: TimeSlot, opts?: { strict?: boolean }): GlobalSlot | null {
  const strict = opts?.strict ?? false;
  try {
    const dayInt = typeof day === "number" ? day : Number(day);
    if (!Number.isInteger(dayInt) || dayInt < 0 || dayInt >= DAYS_IN_WEEK) {
      throw new InvalidTimeSlotError(`Tages-Index außerhalb des Bereichs: ${JSON.stringify(day)}`);
    }
    const [hour, minute] = parseHHMM(time);
    const slotInDay = (hour * 60 + minute) / TIME_SLOT_MINUTES;
    return dayInt * SLOTS_PER_DAY + slotInDay;
  } catch (err) {
    if (strict) {
      throw err instanceof InvalidTimeSlotError ? err : new InvalidTimeSlotError(String(err));
    }
    return null;
  }
}

/** Inverts dayTimeToSlot: global slot index -> [day_index, "HH.MM"]. */
export function slotToDayTime(slot: GlobalSlot): [number, TimeSlot] {
  if (!(Number.isInteger(slot) && slot >= 0 && slot < TOTAL_SLOTS)) {
    throw new InvalidTimeSlotError(`Slot-Index außerhalb des Bereichs: ${JSON.stringify(slot)}`);
  }
  const day = Math.floor(slot / SLOTS_PER_DAY);
  const slotInDay = slot % SLOTS_PER_DAY;
  const [hour, minute] = minutesToHourMinute(slotInDay * TIME_SLOT_MINUTES);
  return [day, formatHHMM(hour, minute)];
}

const DAY_KEY_PATTERN = /^-?\d+$/;

// --- Shape 1: day-index-keyed availability maps ---

export function fromAvailabilityMap(m: AvailabilityMap | null | undefined, opts?: { strict?: boolean }): SlotSet {
  const slots: SlotSet = new Set();
  if (!m) return slots;
  for (const [dayKey, times] of Object.entries(m)) {
    // überspringt Nicht-Tages-Schlüssel wie "mode"/"extraMinutes" in
    // time_range_for_students, die im selben flachen Objekt mitgeführt werden
    if (!DAY_KEY_PATTERN.test(dayKey)) continue;
    if (!Array.isArray(times)) continue;
    for (const time of times) {
      const slot = dayTimeToSlot(dayKey, time, opts);
      if (slot !== null) slots.add(slot);
    }
  }
  return slots;
}

/** Serializes a SlotSet back into Shape 1. Empty days are omitted; output is
 * sorted chronologically by slot index, not lexically by string. */
export function toAvailabilityMap(slots: SlotSet): AvailabilityMap {
  const byDay = new Map<number, GlobalSlot[]>();
  slots.forEach((slot) => {
    const [day] = slotToDayTime(slot);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(slot);
  });
  const result: AvailabilityMap = {};
  for (const day of Array.from(byDay.keys()).sort((a, b) => a - b)) {
    const dayKey = String(day) as DayIndex;
    result[dayKey] = byDay
      .get(day)!
      .sort((a, b) => a - b)
      .map((s) => slotToDayTime(s)[1]);
  }
  return result;
}

/** Single membership check, replacing the scattered
 * .includes(time) || .includes(time.slice(1)) || .includes("0"+time) workarounds. */
export function isSlotAvailable(m: AvailabilityMap | null | undefined, day: DayIndex | number | string, time: TimeSlot): boolean {
  const slot = dayTimeToSlot(day, time);
  if (slot === null) return false;
  return fromAvailabilityMap(m).has(slot);
}

// --- Shape 2: schedules.available_times — {"0".."6": [["HH.MM", member_id], ...]} ---

export function fromScheduleMap(m: ScheduleMap | null | undefined, opts?: { strict?: boolean }): SlotAssignment {
  const strict = opts?.strict ?? false;
  const assignment: SlotAssignment = new Map();
  if (!m) return assignment;
  for (const [dayKey, entries] of Object.entries(m)) {
    if (!Array.isArray(entries)) continue;
    for (const entry of entries as ScheduleSlot[]) {
      if (!Array.isArray(entry) || entry.length < 2) {
        if (strict) throw new InvalidTimeSlotError(`Ungültiger Schedule-Eintrag: ${JSON.stringify(entry)}`);
        continue;
      }
      const [time, memberId] = entry;
      const slot = dayTimeToSlot(dayKey, time, opts);
      if (slot !== null) assignment.set(slot, memberId);
    }
  }
  return assignment;
}

/** Serializes a SlotAssignment back into Shape 2. ALWAYS emits all 7 day keys
 * (matches the validate_schedule_format DB CHECK constraint). */
export function toScheduleMap(assignment: SlotAssignment): ScheduleMap {
  const result = {} as ScheduleMap;
  for (const dayKey of DAY_KEYS) result[dayKey] = [];
  const sortedSlots = Array.from(assignment.keys()).sort((a, b) => a - b);
  for (const slot of sortedSlots) {
    const [day, time] = slotToDayTime(slot);
    result[String(day) as DayIndex].push([time, assignment.get(slot) as string]);
  }
  return result;
}

// --- Shapes 3/4/5: day-name-keyed {DayName: {"HH.MM": member_id}} ---
// Shape 3 = English day names (raw CP-SAT solver output)
// Shape 4 = German uppercase day names (MultiView / PDF export)
// Shape 5 = the frontend's in-memory {DAYNAME:{time:memberId}} working shape
//           (same structural shape as Shape 4 — no separate function needed)

export function fromNamedDayTimeMap(
  m: Record<string, Record<string, string>> | null | undefined,
  dayNames: readonly string[],
  opts?: { strict?: boolean }
): SlotAssignment {
  const strict = opts?.strict ?? false;
  const assignment: SlotAssignment = new Map();
  if (!m) return assignment;
  const nameToIndex = new Map(dayNames.map((name, idx) => [name, idx]));
  for (const [dayName, times] of Object.entries(m)) {
    if (!times || typeof times !== "object") continue;
    const dayIndex = nameToIndex.get(dayName);
    if (dayIndex === undefined) {
      if (strict) throw new InvalidTimeSlotError(`Unbekannter Tagesname: ${JSON.stringify(dayName)}`);
      continue;
    }
    for (const [time, memberId] of Object.entries(times)) {
      const slot = dayTimeToSlot(dayIndex, time, opts);
      if (slot !== null) assignment.set(slot, memberId);
    }
  }
  return assignment;
}

export function toNamedDayTimeMap(assignment: SlotAssignment, dayNames: readonly string[]): Record<string, Record<string, string>> {
  const result: Record<string, Record<string, string>> = {};
  const sortedSlots = Array.from(assignment.keys()).sort((a, b) => a - b);
  for (const slot of sortedSlots) {
    const [day, time] = slotToDayTime(slot);
    const dayName = dayNames[day];
    if (!result[dayName]) result[dayName] = {};
    result[dayName][time] = assignment.get(slot) as string;
  }
  return result;
}

export function fromEnglishDayMap(m: Record<string, Record<string, string>> | null | undefined, opts?: { strict?: boolean }): SlotAssignment {
  return fromNamedDayTimeMap(m, DAY_NAMES_ENGLISH, opts);
}

export function toEnglishDayMap(assignment: SlotAssignment): Record<string, Record<string, string>> {
  return toNamedDayTimeMap(assignment, DAY_NAMES_ENGLISH);
}

export function fromGermanDayMap(m: Record<string, Record<string, string>> | null | undefined, opts?: { strict?: boolean }): SlotAssignment {
  return fromNamedDayTimeMap(m, DAY_NAMES_GERMAN_UPPER, opts);
}

export function toGermanDayMap(assignment: SlotAssignment): Record<string, Record<string, string>> {
  return toNamedDayTimeMap(assignment, DAY_NAMES_GERMAN_UPPER);
}

// --- Range consolidation ---
// Unifies the interval-detection ALGORITHM only; display formatting stays
// with each caller (two genuinely different display conventions exist today —
// dot-separated zero-padded vs. colon-separated unpadded — unifying the
// rendered string would be a visible UI change, out of scope here).

export interface TimeRange {
  start: TimeSlot;
  end: TimeSlot; // exclusive
}

/**
 * Exclusive end of a range that runs to the last slot of the day (23.45).
 * Not a valid slot start — parseHHMM rejects it on purpose.
 */
/**
 * Chronologischer Vergleich zweier Slot-Strings ("9.45" < "10.00"). Ein nacktes
 * `.sort()` sortiert lexikografisch und stellt "10.00" vor "9.45" — daran scheiterte
 * das Erweitern eines Lieblingszeiten-Blocks über die 10-Uhr-Grenze (Audit 2026-09-19).
 */
export function compareTimeSlots(a: TimeSlot, b: TimeSlot): number {
  const [ah, am] = parseHHMM(a);
  const [bh, bm] = parseHHMM(b);
  return (ah * 60 + am) - (bh * 60 + bm);
}

/** Kopie der Liste, chronologisch sortiert. */
export function sortTimeSlots(times: readonly TimeSlot[]): TimeSlot[] {
  return [...times].sort(compareTimeSlots);
}

export const END_OF_DAY: TimeSlot = "24.00";

/**
 * Parses the EXCLUSIVE end of a range. Accepts END_OF_DAY on top of everything
 * parseHHMM takes: consolidateToRanges emits "24.00" for a block ending at the
 * last slot of the day, and parseHHMM must keep rejecting hour 24 because it
 * validates slot STARTS. Callers that render a range end must use this — using
 * parseHHMM there throws on any availability that reaches 23.45.
 */
export function parseRangeEnd(time: TimeSlot): [number, number] {
  if (time === END_OF_DAY) return [24, 0];
  return parseHHMM(time);
}

/** Consolidates a list of "HH.MM" slot strings into contiguous [start, end) ranges. */
export function consolidateToRanges(times: TimeSlot[]): TimeRange[] {
  if (!times || times.length === 0) return [];
  const slots = Array.from(
    new Set(
      times
        .map((t) => dayTimeToSlot(0, t))
        .filter((s): s is GlobalSlot => s !== null)
    )
  ).sort((a, b) => a - b);
  if (slots.length === 0) return [];

  const ranges: TimeRange[] = [];
  let rangeStart = slots[0];
  let prev = slots[0];
  for (let i = 1; i <= slots.length; i++) {
    const current = slots[i];
    if (current !== undefined && current === prev + 1) {
      prev = current;
      continue;
    }
    ranges.push({
      start: slotToDayTime(rangeStart)[1],
      end: formatEndOfRange(prev),
    });
    if (current !== undefined) {
      rangeStart = current;
      prev = current;
    }
  }
  return ranges;
}

function formatEndOfRange(lastSlotInRange: GlobalSlot): TimeSlot {
  const [hour, minute] = minutesToHourMinute((lastSlotInRange % SLOTS_PER_DAY) * TIME_SLOT_MINUTES + TIME_SLOT_MINUTES);
  return formatHHMM(hour, minute);
}
